import React, { useEffect, useMemo, useRef, useState } from 'react';

import { AnswerContent, Button, offersAnswerFormats } from '@copilot/ui';
import * as speechApi from '../../services/speechApi';
import AnswerFeedback from './AnswerFeedback';

/**
 * Derives simple talking points from the answer text client-side (split on
 * bullet markers or sentence boundaries, capped). The backend does not
 * generate structured talking points separately in this pass — this is a
 * reasonable v1 simplification, not a full structured-output feature.
 */
function extractTalkingPoints(answer) {
  if (!answer) return [];

  const rawLines = answer.split('\n').map((line) => line.trim());
  const bulletLines = rawLines
    .filter((line) => /^[-•*]\s*/.test(line))
    .map((line) => line.replace(/^[-•*]\s*/, '').trim())
    .filter(Boolean);

  if (bulletLines.length > 0) {
    return bulletLines.slice(0, 5);
  }

  const sentences = answer
    .split(/(?<=[.!?])\s+/)
    .map((s) => s.trim())
    .filter(Boolean);

  return sentences.slice(0, 4);
}

function CoPilotPanel({
  question,
  answer,
  isGenerating,
  isStreamingAnswer,
  error,
  onRetryAnswer,
  onCorrectAnswer,
  onRegenerateAnswer,
  questionTranscriptId,
  showTalkingPoints = true,
  meetingId,
  aiResponseId,
  answerRevision,
  ttsAvailable,
  codeRelated,
}) {
  const [copied, setCopied] = useState(false);

  // Inline "this answer is wrong, here's what they actually mean" box.
  // The correction is sent over the live audio socket (see
  // LiveMeetingPage's handleCorrectAnswer) and the backend re-answers
  // this same turn using it.
  const [showCorrectionBox, setShowCorrectionBox] = useState(false);
  const [correctionText, setCorrectionText] = useState('');

  // Collapse and clear the box whenever the panel moves to a different
  // answer, so a correction can never land on the wrong turn.
  useEffect(() => {
    setShowCorrectionBox(false);
    setCorrectionText('');
  }, [questionTranscriptId]);

  const handleSubmitCorrection = () => {
    const guidance = correctionText.trim();
    if (!guidance || !onCorrectAnswer || !questionTranscriptId) return;

    onCorrectAnswer(questionTranscriptId, guidance);
    setCorrectionText('');
    setShowCorrectionBox(false);
  };

  const [isSynthesizing, setIsSynthesizing] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [ttsError, setTtsError] = useState('');

  const audioRef = useRef(null);
  const objectUrlRef = useRef(null);

  const stopPlayback = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }

    if (objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current);
      objectUrlRef.current = null;
    }

    setIsPlaying(false);
  };

  // A new answer (or leaving the panel) invalidates any in-flight/playing
  // audio for the previous one - never let stale audio keep playing.
  useEffect(() => {
    stopPlayback();
    setTtsError('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aiResponseId]);

  useEffect(() => stopPlayback, []);

  const handleCopy = async () => {
    if (!answer) return;

    try {
      await navigator.clipboard.writeText(answer);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (_) {
      // Clipboard API may be unavailable; fail silently.
    }
  };

  const handleReadAloud = async () => {
    setTtsError('');
    setIsSynthesizing(true);

    try {
      const blob = await speechApi.synthesizeAnswer({
        meetingId,
        aiResponseId,
      });

      const url = URL.createObjectURL(blob);
      const audio = new Audio(url);

      audioRef.current = audio;
      objectUrlRef.current = url;

      audio.addEventListener('ended', stopPlayback);
      audio.addEventListener('error', stopPlayback);

      await audio.play();
      setIsPlaying(true);
    } catch (err) {
      setTtsError(err.message || 'Unable to read the answer aloud.');
      stopPlayback();
    } finally {
      setIsSynthesizing(false);
    }
  };

  const canReadAloud =
    !!answer && !isGenerating && !!aiResponseId && ttsAvailable !== false;

  // extractTalkingPoints re-scans the full answer string (split + regex
  // passes), and cost grows with answer length. While the answer is
  // still streaming in, `answer` changes on every single delta, so
  // recomputing on every keystroke-sized chunk is pure wasted CPU on
  // text that is about to change again a moment later - the points are
  // only shown/used once the answer has settled. Skip the recompute
  // while streaming; the finished text is scanned once, on completion.
  //
  // MUST stay above the `!question` early return below: every hook in
  // this component has to run on every render, in the same order,
  // regardless of `question`/`answer` state - a hook after a
  // conditional return only fires on SOME renders, which is exactly
  // the "Rendered more hooks than during the previous render" crash
  // this guards against. The condition (`showTalkingPoints &&
  // !isStreamingAnswer && question`) lives inside the memo callback
  // instead, where it's free to vary without affecting hook order.
  const talkingPoints = useMemo(
    () =>
      question && showTalkingPoints && !isStreamingAnswer
        ? extractTalkingPoints(answer)
        : [],
    [question, showTalkingPoints, isStreamingAnswer, answer]
  );

  if (!question) {
    return (
      <div className="empty-state">
        <div className="empty-state-title">Waiting for the conversation</div>
        <p>
          KrishvaQ&apos;s response to the latest point in the meeting
          will appear here.
        </p>
      </div>
    );
  }

  return (
    <div>
      <div className="copilot-current-question">
        <div className="copilot-label">Participant</div>
        <p className="copilot-question">{question}</p>
        {/* Same question, answered again in a different shape - only for a
            code-related question. Uses the meeting's programming languages;
            the new answer is a new revision of this one (rated separately). */}
        {onRegenerateAnswer &&
          questionTranscriptId &&
          !!answer &&
          !isGenerating &&
          offersAnswerFormats(codeRelated, answer) && (
            <div className="copilot-regenerate" aria-label="Regenerate this answer">
              <button
                type="button"
                className="copilot-feedback-btn"
                onClick={() => onRegenerateAnswer(questionTranscriptId, 'code')}
              >
                Regenerate as Code
              </button>
              <button
                type="button"
                className="copilot-feedback-btn"
                onClick={() => onRegenerateAnswer(questionTranscriptId, 'theory')}
              >
                Explain as Theory
              </button>
            </div>
          )}
      </div>

      <div className="copilot-answer-card">
        <div className="copilot-label">
          KrishvaQ Response
          {isStreamingAnswer && answer && (
            <span style={{ marginLeft: 'var(--space-2)' }}>· streaming</span>
          )}
        </div>

        {error ? (
          <div role="status">
            {/*
              `error` is the backend's classified message - it
              distinguishes a provider blip (worth retrying now) from an
              exhausted quota (which will not fix itself). This used to
              render one hardcoded sentence for every cause.
            */}
            <p style={{ color: 'var(--color-danger)' }}>{error}</p>
            <p className="copilot-answer-note">
              Meeting transcription is still active.
            </p>
            {onRetryAnswer && questionTranscriptId && (
              <button
                type="button"
                className="copilot-retry-button"
                onClick={() => onRetryAnswer(questionTranscriptId)}
              >
                Retry answer
              </button>
            )}
          </div>
        ) : isGenerating && !answer ? (
          <p className="copilot-answer" aria-live="polite">
            Thinking...
          </p>
        ) : (
          // Code in the answer (``` fences) shows as a code block.
          <div aria-live="polite">
            <AnswerContent className="copilot-answer" text={answer} />
          </div>
        )}

        <div className="copilot-actions">
          <Button
            variant="secondary"
            onClick={handleCopy}
            disabled={!answer || isGenerating}
          >
            {copied ? 'Copied!' : 'Copy'}
          </Button>

          <Button
            variant="secondary"
            onClick={isPlaying ? stopPlayback : handleReadAloud}
            disabled={!canReadAloud || isSynthesizing}
          >
            {isSynthesizing
              ? 'Loading audio...'
              : isPlaying
              ? 'Stop'
              : 'Read Aloud'}
          </Button>

          {ttsError && (
            <span style={{ color: 'var(--color-danger)', fontSize: '0.85rem' }}>
              {ttsError}
            </span>
          )}

          {ttsAvailable === false && (
            <span style={{ color: 'var(--color-text-muted)', fontSize: '0.85rem' }}>
              Read Aloud isn&apos;t available for this answer&apos;s language.
            </span>
          )}

          {onCorrectAnswer && !!answer && !isGenerating && (
            <Button
              variant="secondary"
              onClick={() => setShowCorrectionBox((v) => !v)}
            >
              {showCorrectionBox ? 'Cancel' : 'Not right? Correct it'}
            </Button>
          )}
        </div>



        {/* Rating for the COMPLETED answer shown. Keyed by response + revision
            (+ length when an older backend sends no revision) so a regenerated
            answer starts with its own, unrated state. */}
        {!!answer && !isGenerating && !error && !!aiResponseId && (
          <AnswerFeedback
            key={`${aiResponseId}:${answerRevision ?? answer.length}`}
            aiResponseId={aiResponseId}
            answerRevision={answerRevision}
          />
        )}

        {showCorrectionBox && (
          <div className="copilot-correction-box">
            <label className="copilot-label" htmlFor="copilot-correction-input">
              What&apos;s wrong, and what are they actually asking?
            </label>
            <textarea
              id="copilot-correction-input"
              className="copilot-correction-input"
              rows={3}
              value={correctionText}
              placeholder="e.g. This is off — the client is asking about retrieval-augmented generation, not ranking models."
              onChange={(e) => setCorrectionText(e.target.value)}
              maxLength={1000}
            />
            <div className="copilot-actions">
              <Button
                variant="primary"
                onClick={handleSubmitCorrection}
                disabled={!correctionText.trim()}
              >
                Send correction
              </Button>
            </div>
          </div>
        )}
      </div>

      {talkingPoints.length > 0 && (
        <div className="copilot-talking-points">
          <div className="copilot-label">Talking Points</div>

          <ul>
            {talkingPoints.map((point, idx) => (
              <li key={idx}>{point}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

export default CoPilotPanel;
